import { useEffect, useMemo, useState } from "react";
import { Calendar, ExternalLink, User, Clock, Video, RefreshCw, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import UnassignedBookings from "@/components/UnassignedBookings";

/**
 * AdminOfficeHours — "Student Calls" admin dashboard.
 *
 * Renders upcoming Calendly bookings only (future-dated). Past bookings hidden.
 * Admin can delete a booking locally — Calendly itself must be canceled separately
 * if you want to free the slot back up.
 */

const CALENDLY_BOOK_URL = process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/event_types";

function fmtDay(iso) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const target = new Date(`${iso}T00:00:00`);
  if (target.toDateString() === today.toDateString()) return "Today";
  if (target.toDateString() === tomorrow.toDateString()) return "Tomorrow";
  return target.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

function fmtTime(booking) {
  // Prefer the timezone-aware ISO string — converts UTC → browser local correctly.
  if (booking && booking.start_time_iso) {
    const d = new Date(booking.start_time_iso);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    }
  }
  // Fallback: legacy bookings only have HH:MM in UTC. Convert manually.
  if (!booking || !booking.time || !booking.date) return "—";
  const iso = `${booking.date}T${booking.time}:00Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return booking.time;
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function localDateKey(booking) {
  // Group by the LOCAL date, not the stored UTC date.
  const src = booking.start_time_iso || (booking.date && booking.time ? `${booking.date}T${booking.time}:00Z` : null);
  if (!src) return booking.date;
  const d = new Date(src);
  if (Number.isNaN(d.getTime())) return booking.date;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AdminOfficeHours() {
  const [rows, setRows] = useState([]);
  const [unmatched, setUnmatched] = useState({ bookings: [], students: [] });
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadUnmatched = async () => {
    try {
      const { data } = await api.get("/admin/unmatched-bookings");
      setUnmatched(data);
    } catch (e) {
      /* non-blocking */
    }
  };

  const syncCalendly = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/calendly/sync");
      toast.success(
        data.attached
          ? `${data.attached} missing call${data.attached === 1 ? "" : "s"} added to student calendars`
          : data.unmatched
            ? `${data.unmatched} booking${data.unmatched === 1 ? "" : "s"} need assigning`
            : "Everything is already in sync",
      );
      await Promise.all([load(), loadUnmatched()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calendly sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/calendly-bookings");
      // Drop truly-past bookings by comparing actual local timestamp to now.
      const now = Date.now();
      const upcoming = (data || []).filter((r) => {
        const src = r.start_time_iso || (r.date && r.time ? `${r.date}T${r.time}:00Z` : null);
        if (!src) return true;
        const t = new Date(src).getTime();
        return Number.isNaN(t) ? true : t >= now - 60 * 60 * 1000; // 1h grace so calls in progress still show
      });
      setRows(upcoming);
    } catch (e) {
      toast.error("Could not load calls");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadUnmatched(); }, []);

  const byDay = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const key = localDateKey(r);
      (m[key] = m[key] || []).push(r);
    }
    // Also re-sort each day by actual local time
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => {
        const ta = new Date(a.start_time_iso || `${a.date}T${a.time || "00:00"}:00Z`).getTime();
        const tb = new Date(b.start_time_iso || `${b.date}T${b.time || "00:00"}:00Z`).getTime();
        return ta - tb;
      });
    }
    return m;
  }, [rows]);

  const today = new Date().toISOString().slice(0, 10);
  const todayLocal = (() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  })();
  const todayCount = rows.filter((r) => localDateKey(r) === todayLocal).length;
  const next7 = rows.filter((r) => {
    const d = new Date(`${r.date}T00:00:00`);
    const limit = new Date(); limit.setDate(limit.getDate() + 7);
    return d <= limit;
  }).length;

  const removeBooking = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/calendly-bookings/${confirmDelete.id}`);
      toast.success("Call removed");
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    }
  };

  return (
    <div data-testid="admin-office-hours">
      <div className="section-tag mb-3"><span className="dot" /> Coaching</div>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <h1 className="h-display text-3xl sm:text-4xl">Student Calls</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={syncCalendly}
            disabled={syncing}
            className="px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition hover:bg-white/5"
            style={{ color: "var(--text)", border: "1px solid var(--border)" }}
            title="Pull bookings straight from Calendly and fix any that never reached a student"
            data-testid="office-hours-sync"
          >
            {syncing ? <span className="spinner" /> : <RefreshCw size={14} />}
            {syncing ? "Syncing..." : "Sync Calendly"}
          </button>
          <button
            onClick={load}
            className="p-2 rounded-xl hover:bg-white/5 transition"
            style={{ color: "var(--text-muted)", border: "1px solid var(--border)" }}
            title="Refresh"
            data-testid="office-hours-refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
          <a
            href={CALENDLY_BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition"
            style={{ background: "var(--lime)", color: "#0c0c0c" }}
            data-testid="office-hours-manage"
          >
            <ExternalLink size={14} /> Manage in Calendly
          </a>
        </div>
      </div>
      <p className="text-sm mb-8 max-w-2xl" style={{ color: "var(--text-muted)" }}>
        Coaching calls flow in automatically from Calendly. Manage your availability, event types,
        and cancellations directly in Calendly. This page shows upcoming calls only.
      </p>

      <UnassignedBookings
        bookings={unmatched.bookings}
        students={unmatched.students}
        onChange={() => { load(); loadUnmatched(); }}
      />

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Stat label="Calls today" val={todayCount} accent />
        <Stat label="Next 7 days" val={next7} />
        <Stat label="All upcoming" val={rows.length} />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><span className="spinner" /></div>
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-6">
          {Object.entries(byDay).map(([day, list]) => (
            <div key={day}>
              <div className="font-mono text-xs uppercase tracking-wider mb-3" style={{ color: "var(--lime)" }}>
                {fmtDay(day)}
              </div>
              <div className="space-y-2">
                {list.map((b) => (
                  <BookingRow
                    key={b.id}
                    booking={b}
                    onDelete={() => setConfirmDelete(b)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove this call?"
        description={`This removes "${confirmDelete?.title || "the call"}" from ${confirmDelete?.student_name || confirmDelete?.student_email || "the student"}'s calendar. To free up the time slot in Calendly, you'll also need to cancel it there.`}
        destructive
        confirmLabel="Remove"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={removeBooking}
      />
    </div>
  );
}

function BookingRow({ booking, onDelete }) {
  const joinUrl = (booking.notes || "").startsWith("Join: ") ? booking.notes.slice(6) : null;
  return (
    <div
      className="surface-card p-4 flex items-center justify-between gap-4 flex-wrap"
      data-testid={`booking-${booking.id}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <Clock size={16} style={{ color: "var(--lime)" }} />
        <div className="min-w-0 flex-1">
          <div className="font-mono text-sm">{fmtTime(booking)}</div>
          <div className="text-xs mt-0.5 truncate" style={{ color: "var(--text)" }}>{booking.title || "Coaching session"}</div>
          <div className="text-xs mt-0.5 flex items-center gap-1.5 truncate" style={{ color: "var(--text-muted)" }}>
            <User size={11} /> {booking.student_name || booking.student_email || "Unknown student"}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {joinUrl && (
          <a
            href={joinUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl transition"
            style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text-muted)" }}
            data-testid={`booking-join-${booking.id}`}
          >
            <Video size={12} /> Join
          </a>
        )}
        <button
          onClick={onDelete}
          className="p-2 rounded-xl hover:bg-white/5 transition"
          style={{ color: "#f87171", border: "1px solid rgba(248,113,113,0.25)" }}
          title="Remove call"
          data-testid={`booking-delete-${booking.id}`}
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="surface-card p-12 text-center">
      <Calendar size={28} className="mx-auto mb-4" style={{ color: "var(--text-muted)" }} />
      <h3 className="font-semibold mb-1">No upcoming calls</h3>
      <p className="text-sm mb-5 max-w-md mx-auto" style={{ color: "var(--text-muted)" }}>
        Once a student books a call through Calendly, it will show up here automatically.
        To add availability or change event types, head into your Calendly dashboard.
      </p>
      <a
        href={CALENDLY_BOOK_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
        style={{ background: "var(--lime)", color: "#0c0c0c" }}
      >
        <ExternalLink size={14} /> Open Calendly
      </a>
    </div>
  );
}

function Stat({ label, val, accent }) {
  return (
    <div className="surface-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="font-mono text-2xl" style={{ color: accent ? "var(--lime)" : "var(--text)" }}>{val}</div>
    </div>
  );
}
